/**
 * Maintenance Job Descriptions
 * Editable descriptions and checklists for maintenance jobs
 */
import { devLog } from '../utils/devLog';
// Phase 2.6: Temporarily disabled for System B removal - will be restored in Phase 3
// import { useVesselCollections } from '../hooks/useVesselCollections';
import { COLLECTIONS, createBaseRecord } from '../types/core';
import { metaKey } from '../utils/metaKey';

export const JOB_DESCRIPTIONS = {
  // Fire Fighting Equipment
  'Fire Extinguisher Visual': {
    description: 'Monthly visual check of fire extinguishers',
    scope: 'Check pressure, condition and accessibility',
    checklist: [
      'Check pressure gauge in green',
      'Verify pin and seal intact', 
      'Check for damage or corrosion',
      'Ensure bracket secure',
      'Verify not blocked',
      'Check signage visible'
    ],
    tools: ['Torch'],
    estimatedTime: '2 minutes per unit',
    safetyNote: 'Do not discharge during inspection'
  },

  'Fire Extinguisher Service': {
    description: 'Annual service by competent person',
    scope: 'Full service including discharge test if required',
    checklist: [
      'Perform visual inspection',
      'Check and record pressure',
      'Weigh CO2 extinguishers',
      'Check hose and nozzle condition',
      'Test discharge mechanism (if due)',
      'Replace seals and pins',
      'Update service tag',
      'Record in maintenance log'
    ],
    tools: ['Pressure gauge', 'Scales', 'Service kit'],
    estimatedTime: '15-20 minutes per unit',
    safetyNote: 'Only qualified personnel should service extinguishers'
  },

  'Fire Blanket Check': {
    description: 'Monthly check of fire blankets',
    scope: 'Visual inspection and accessibility check',
    checklist: [
      'Check container is sealed',
      'Verify quick-release mechanism works',
      'Check for damage to container',
      'Ensure location signage is clear',
      'Verify blanket is accessible'
    ],
    tools: [],
    estimatedTime: '1-2 minutes per unit'
  },

  // Life Saving Appliances
  'Lifejacket Check': {
    description: 'Monthly lifejacket inspection',
    scope: 'Check condition and accessories',
    checklist: [
      'Check fabric - no tears',
      'Test buckles and zips',
      'Check whistle attached',
      'Test light (if fitted)',
      'Check reflective tape',
      'Verify service date'
    ],
    tools: ['Torch'],
    estimatedTime: '2 minutes per jacket'
  },

  'Lifejacket Monthly Check': {
    description: 'Monthly lifejacket inspection',
    scope: 'Check condition and accessories',
    checklist: [
      'Check fabric - no tears',
      'Test buckles and zips',
      'Check whistle attached',
      'Test light (if fitted)',
      'Check reflective tape',
      'Verify service date'
    ],
    tools: ['Torch'],
    estimatedTime: '2 minutes per jacket'
  },

  'Foam Lifejacket Light Test': {
    description: 'Monthly light test for foam lifejackets',
    scope: 'Test lifejacket lights function',
    checklist: [
      'Activate each light',
      'Check brightness adequate',
      'Check battery indicator',
      'Replace if faulty'
    ],
    tools: [],
    estimatedTime: '1 minute per jacket'
  },

  'Foam Lifejacket Annual Inspection': {
    description: 'Annual foam lifejacket inspection',
    scope: 'Thorough condition check',
    checklist: [
      'Check buoyancy foam intact',
      'Test all fastenings',
      'Check reflective tape',
      'Test whistle and light',
      'Check for UV damage',
      'Record inspection date'
    ],
    tools: ['Inspection form'],
    estimatedTime: '3 minutes per jacket'
  },

  'Inflatable Lifejacket Annual Service': {
    description: 'Annual inflatable lifejacket service',
    scope: 'Professional service required',
    checklist: [
      'Check gas cylinder weight',
      'Test inflation mechanism',
      'Check bladder integrity',
      'Replace service indicators',
      'Test oral inflation',
      'Update service record'
    ],
    tools: ['Service kit', 'Scales'],
    estimatedTime: '15 minutes per jacket',
    safetyNote: 'Service by competent person only'
  },

  'Liferaft Visual': {
    description: 'Monthly liferaft visual check',
    scope: 'Check mounting and service status',
    checklist: [
      'Check cradle secure',
      'Verify HRU date (if fitted)',
      'Check painter secured',
      'Clear launch path',
      'Check service date',
      'Check for damage'
    ],
    tools: ['Torch'],
    estimatedTime: '3 minutes per raft'
  },

  'Liferaft Monthly Inspection': {
    description: 'Monthly liferaft visual check',
    scope: 'Check mounting and service status',
    checklist: [
      'Check cradle secure',
      'Verify HRU date (if fitted)',
      'Check painter secured',
      'Clear launch path',
      'Check service date',
      'Check for damage'
    ],
    tools: ['Torch'],
    estimatedTime: '3 minutes per raft'
  },

  'Lifebuoy Check': {
    description: 'Monthly inspection of lifebuoys and attached equipment',
    scope: 'Check condition, markings, lights, and ensure quick release capability',
    checklist: [
      'Check buoyancy material condition',
      'Verify grab line secure',
      'Check retro-reflective tape',
      'Test light function (if fitted)',
      'Check quick release mechanism',
      'Verify throw line condition',
      'Check vessel name marking',
      'Ensure mounting allows quick release'
    ],
    tools: [],
    estimatedTime: '2-3 minutes per buoy'
  },

  'Lifebuoy Monthly Inspection': {
    description: 'Quick monthly check of lifebuoys',
    scope: 'Visual inspection and function test',
    checklist: [
      'Check buoy condition',
      'Test light (if fitted)',
      'Check grab line attached',
      'Verify quick release works',
      'Check reflective tape visible'
    ],
    tools: [],
    estimatedTime: '2 minutes per buoy'
  },

  // Medical
  'First Aid Kit Check': {
    description: 'Monthly first aid kit inventory',
    scope: 'Check expiry dates and replenish items',
    checklist: [
      'Check medication expiry dates',
      'Verify bandages present',
      'Check sterile items sealed',
      'Replenish used items',
      'Update inventory list'
    ],
    tools: ['Inventory list'],
    estimatedTime: '10 minutes per kit'
  },

  // Navigation Equipment
  'Navigation Lights Check': {
    description: 'Weekly check of navigation lights',
    scope: 'Test all navigation and anchor lights',
    checklist: [
      'Test port (red) light',
      'Test starboard (green) light',
      'Test stern (white) light',
      'Test masthead light(s)',
      'Test anchor light',
      'Check for corrosion',
      'Clean lenses if needed',
      'Check spare bulbs available'
    ],
    tools: ['Cleaning cloth', 'Spare bulbs'],
    estimatedTime: '10-15 minutes'
  },

  // Emergency Equipment
  'EPIRB Test': {
    description: 'Monthly self-test of EPIRB',
    scope: 'Perform self-test and check mounting',
    checklist: [
      'Perform self-test function',
      'Check LED indicators',
      'Verify HRU expiry (if fitted)',
      'Check mounting bracket secure',
      'Verify registration current',
      'Check battery expiry date',
      'Clean contacts if needed',
      'Record test in log'
    ],
    tools: [],
    estimatedTime: '5 minutes',
    safetyNote: 'Do NOT activate emergency transmission'
  },

  // General Equipment
  'Emergency Equipment Check': {
    description: 'General emergency equipment inspection',
    scope: 'Check all emergency equipment is present and serviceable',
    checklist: [
      'Check emergency tiller present',
      'Verify emergency steering instructions posted',
      'Check emergency tools present',
      'Verify emergency communications work',
      'Check emergency lighting',
      'Verify emergency procedures posted',
      'Check muster list current',
      'Verify alarm signals work'
    ],
    tools: ['Test equipment'],
    estimatedTime: '20-30 minutes'
  }
};

/**
 * Get job description for a specific job
 */
export function getJobDescription(jobType) {
  devLog('Getting job description for:', jobType);
  const description = JOB_DESCRIPTIONS[jobType];
  if (description) {
    devLog('Found description:', description);
    return description;
  }
  devLog('No description found, using default');
  return {
    description: jobType,
    scope: 'Perform required maintenance',
    checklist: [],
    tools: [],
    estimatedTime: 'As required'
  };
}

/**
 * Phase 2.6: Temporarily disabled for System B removal - will be restored in Phase 3
 * Update job description (for user customization)
 * Now uses collections with vessel scoping
 */
export async function updateJobDescription(jobType, updates, vesselId, collections) {
  console.warn('Phase 2.6: Job description update disabled - will return in Phase 3');
  return;

  // if (!vesselId || !collections) {
  //
  //   return;
  // }
  //
  // const customKey = metaKey('custom_job_descriptions', vesselId);
  //
  // try {
  //   // Get existing custom descriptions
  //   const existing = await collections.get('meta', customKey);
  //   const customDescriptions = existing?.data || {};
  //
  //   // Update the specific job description
  //   customDescriptions[jobType] = {
  //     ...JOB_DESCRIPTIONS[jobType],
  //     ...updates,
  //     customized: true,
  //     lastModified: new Date().toISOString()
  //   };
  //
  //   // Save back to collections
  //   await collections.upsert('meta', createBaseRecord({
  //     id: customKey,
  //     vesselId,
  //     data: customDescriptions,
  //     origin: 'manual'
  //   }));
  // } catch (error) {
  //
  // }
}

/**
 * Phase 2.6: Temporarily simplified - returns defaults only during System B removal
 * Get all job descriptions including custom ones
 * Now uses collections with vessel scoping
 */
export async function getAllJobDescriptions(vesselId, collections) {
  // Phase 2.6: Return default descriptions only (no custom loading)
  console.warn('Phase 2.6: Custom job descriptions disabled - returning defaults only');
  return JOB_DESCRIPTIONS;

  // if (!vesselId || !collections) {
  //
  //   return JOB_DESCRIPTIONS;
  // }
  //
  // try {
  //   const customKey = metaKey('custom_job_descriptions', vesselId);
  //   const existing = await collections.get('meta', customKey);
  //   const customDescriptions = existing?.data || {};
  //
  //   return {
  //     ...JOB_DESCRIPTIONS,
  //     ...customDescriptions
  //   };
  // } catch (error) {
  //
  //   return JOB_DESCRIPTIONS;
  // }
}